import React from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { MonthProvider } from "./context/MonthContext";
import LoginModal from "./components/LoginModal";
import Header from "./components/Header";
import ExpenseEntry from "./components/ExpenseEntry";
import IncomeEntry from "./components/IncomeEntry";
import AssetManager from "./components/AssetManager";

import MonthlyDashboard from "./components/MonthlyDashboard";
import AnnualOverview from "./components/AnnualOverview";
import { Transaction } from "./types";
import { transactionApi } from "./services/api";
import "./App.css";

const AppContent: React.FC = () => {
  const { user, isLoggedIn, isLoading } = useAuth();
  const [refreshTrigger, setRefreshTrigger] = React.useState(0);

  const handleFinanceChanged = React.useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);
  
  const handleAddTransaction = async (transaction: Omit<Transaction, "id">) => {
    if (!user?.group_id || !user?.user_id) {
      console.error("User not properly authenticated or no group");
      return;
    }

    try {
      await transactionApi.createTransaction({
        date: transaction.date,
        description: transaction.description,
        category: transaction.category,
        amount: Math.abs(transaction.amount), 
        payer_id: user.user_id,
        group_id: user.group_id,
      });
      
      // Trigger refresh of monthly and annual summaries
      handleFinanceChanged();
    } catch (error) {
      console.error("Failed to create transaction:", error);
    }
  };

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#fdfbf7" }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700 mx-auto mb-4"></div>
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: "#fdfbf7", color: "#3a3531" }}
    >
      {!isLoggedIn && <LoginModal />}

      <div
        className={`transition-all duration-300 ${
          !isLoggedIn ? "blur-sm pointer-events-none" : ""
        }`}
      >
        <div className="container mx-auto p-4 sm:p-6 lg:p-8 max-w-7xl">
          <Header />

          <main>
            {!user?.group_id ? (
              <div className="text-center py-12">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md mx-auto">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                    需要加入群组
                  </h3>
                  <p className="text-yellow-700 mb-4">
                    您需要创建或加入一个群组才能开始记录交易。请联系管理员或创建新群组。
                  </p>
                  <p className="text-sm text-yellow-600">
                    群组功能允许多个用户共享财务数据和协作管理支出。
                  </p>
                </div>
              </div>
            ) : (
              <>
                <ExpenseEntry onAddTransaction={handleAddTransaction} />
                <MonthlyDashboard
                  refreshTrigger={refreshTrigger}
                  onBudgetChanged={handleFinanceChanged}
                />
                <IncomeEntry
                  refreshTrigger={refreshTrigger}
                  onIncomeChanged={handleFinanceChanged}
                />
                <AnnualOverview refreshTrigger={refreshTrigger} />
                <AssetManager refreshTrigger={refreshTrigger} />
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <MonthProvider>
        <AppContent />
      </MonthProvider>
    </AuthProvider>
  );
};

export default App;
